import { Box, Button, DialogContent, DialogContentText, DialogTitle, Divider, Stack } from "@mui/material"
import { useEffect, useState } from "react"
import { NexAlert } from "../../components/Alert/Alert"
import { FlightLineConflict, FlightLineEventErrorMessage } from "../../models/MissionUpload"
import { MissionFlightlineErrors } from "../../utils/ValidateFlightlineEventUtils"
import { useFlightlineEventStore } from "../../zstore/FlightlineEventStore"

interface MissionValidatorDialogProps {
    flightlineEventErrors: MissionFlightlineErrors[]
}

enum ShowWhatLines {
    SHOW_CONFLICTS = "showConflictLines",
    SHOW_NO_MATCH = "showNoMatchLines",
}

export const MissionValidatorDialog = ({ flightlineEventErrors }: MissionValidatorDialogProps) => {
    const [flightlineErrorMessages, setFlightlineErrorMessages] = useState<FlightLineEventErrorMessage[]>([])
    const selectedFlightplans = useFlightlineEventStore((state) => state.selectedFlightplans)

    useEffect(() => {
        const errorMessageArray: FlightLineEventErrorMessage[] = []
        // Go through every error object and combine conflicting lines names assigned with the same flightplan ID
        flightlineEventErrors.forEach((error) => {
            let conflictLines: FlightLineConflict[] = []
            for (const [commonPlanId, allConflictingLineNames] of Object.entries(error.conflictErrors)) {
                const flight_plan_name =
                    selectedFlightplans.find((fp) => fp.flight_plan_id === Number(commonPlanId))?.flight_plan_name ??
                    commonPlanId
                conflictLines = [
                    ...conflictLines,
                    {
                        conflictingLineNames: allConflictingLineNames,
                        planName: flight_plan_name,
                    },
                ]
            }

            errorMessageArray.push({
                missionName: error.missionName,
                conflictLines,
                noMatchLines: error.noMatchErrors,
                showConflictLines: false,
                showNoMatchLines: false,
                totalLines: error.numberOfFlightLines,
            })
        })

        setFlightlineErrorMessages(errorMessageArray)
    }, [flightlineEventErrors])

    const handleShowLines = (whichLines: ShowWhatLines, missionName: string) => {
        const changeBool = (key: keyof FlightLineEventErrorMessage) =>
            flightlineErrorMessages.map((flError) => {
                if (flError.missionName === missionName) {
                    return {
                        ...flError,
                        [key]: !flError[key],
                    }
                }
                return flError
            })
        const currentMissionObj = changeBool(whichLines)
        setFlightlineErrorMessages(currentMissionObj)
    }

    return (
        <>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogContent sx={{ pb: 0 }}>
                {flightlineErrorMessages?.map((errorObject, index) => (
                    <Stack key={index} spacing={2} sx={{ py: 2 }}>
                        <DialogContentText variant={"h6"}>
                            The following errors occurred in {errorObject.missionName}:
                        </DialogContentText>
                        {errorObject?.conflictLines?.map((lines, index) => (
                            <ValidatorDialogSegment
                                key={index}
                                severity="warning"
                                text={`${lines.conflictingLineNames.length}/${errorObject.totalLines} flight lines 
                                    matched more than one selected plan. Plan "${lines.planName}" will be assumed.`}
                                onClick={() => handleShowLines(ShowWhatLines.SHOW_CONFLICTS, errorObject.missionName)}
                                linesHidden={!errorObject.showConflictLines}
                                bottomText={errorObject.showConflictLines ? lines?.conflictingLineNames.join(", ") : ""}
                            />
                        ))}
                        {!!errorObject.noMatchLines?.length && (
                            <ValidatorDialogSegment
                                divider={!!errorObject?.conflictLines?.length}
                                severity="error"
                                text={`${errorObject.noMatchLines.length}/${errorObject.totalLines} flight lines could 
                                    not be found in the selected flightplans. They will be set to Ignored status.`}
                                onClick={() => handleShowLines(ShowWhatLines.SHOW_NO_MATCH, errorObject.missionName)}
                                linesHidden={!errorObject.showNoMatchLines}
                                bottomText={errorObject.showNoMatchLines ? errorObject.noMatchLines.join(", ") : ""}
                            />
                        )}
                    </Stack>
                ))}
            </DialogContent>
        </>
    )
}

interface ValidatorDialogSegmentProps {
    text: string
    onClick: () => void
    bottomText?: string
    divider?: boolean
    linesHidden?: boolean
    severity: "success" | "error" | "info" | "warning"
}

const ValidatorDialogSegment = ({
    text,
    onClick,
    bottomText,
    divider,
    linesHidden,
    severity,
}: ValidatorDialogSegmentProps) => {
    return (
        <Stack spacing={2} sx={{ display: "flex", flexDirection: "column" }}>
            {divider && <Divider variant={"middle"} sx={{ mb: 2 }} />}

            <NexAlert severity={severity} disableGutter>
                {text}
            </NexAlert>
            <Box>
                <Button size={"small"} variant={"outlined"} onClick={onClick}>
                    {linesHidden ? "Show" : "Hide"} Lines
                </Button>
            </Box>

            {bottomText && <DialogContentText>{bottomText}</DialogContentText>}
        </Stack>
    )
}
